import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials, faqData } from '@/mocks/movies';
import { useState } from 'react';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们', href: '/about' },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.qiuxiayy.com',
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `${siteInfo.name}品牌介绍 - ${siteInfo.description}`,
    url: 'https://www.qiuxiayy.com/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.qiuxiayy.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.qiuxiayy.com/about' },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-12">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                关于秋霞影院
              </h1>
              <p className="text-sm text-foreground-500">
                了解我们的故事、使命与愿景
              </p>
            </div>

            <section className="mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-5">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-400 leading-relaxed">
                    <p>
                      <strong className="text-foreground-200">秋霞影院</strong>成立于{siteInfo.founded}年，是国内领先的免费在线影视资源聚合平台。我们致力于为广大影迷提供最全面、最及时、最高清的影视内容服务，让每一位用户都能轻松享受观影的乐趣。
                    </p>
                    <p>
                      经过多年发展，秋霞影院已经积累了超过<strong className="text-accent-400">{siteInfo.totalMovies}</strong>部影视资源，涵盖电影、电视剧、综艺、动漫、纪录片等多种类型。无论是院线热映大片、经典老片还是小众佳作，都能在这里找到。
                    </p>
                    <p>
                      我们的日均访问用户超过<strong className="text-accent-400">{siteInfo.dailyVisitors}</strong>，凭借稳定的服务器、流畅的播放体验和清爽无广告的界面设计，赢得了广大用户的信赖与好评。
                    </p>
                  </div>
                </div>
                <div className="relative rounded-xl overflow-hidden aspect-[16/10]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20home%20cinema%20setup%20with%20large%20screen%20and%20comfortable%20seating%2C%20warm%20ambient%20lighting%2C%20dark%20interior%20design%2C%20premium%20movie%20watching%20experience%2C%20cozy%20atmosphere%2C%20professional%20photography&width=1200&height=750&seq=about-cinema&orientation=landscape"
                    alt="秋霞影院品牌形象"
                    title="秋霞影院 - 品牌形象展示"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            <section className="mb-16 bg-background-100/50 rounded-2xl p-8 md:p-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="bg-background-50 rounded-xl p-6 border border-background-200/50"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-100">{t.name}</h3>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <i
                              key={j}
                              className={`text-xs ${j < t.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-foreground-600'}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      "{t.content}"
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-8 text-center">
                联系我们
              </h2>
              <div className="max-w-[600px] mx-auto bg-background-100/50 rounded-2xl p-8 md:p-10">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500/10 flex-shrink-0">
                      <i className="ri-mail-line text-accent-400 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">电子邮箱</div>
                      <a
                        href={`mailto:${siteInfo.email}`}
                        className="text-sm text-foreground-200 hover:text-accent-400 transition-colors cursor-pointer"
                      >
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500/10 flex-shrink-0">
                      <i className="ri-time-line text-accent-400 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">运营时间</div>
                      <div className="text-sm text-foreground-200">7×24小时全天候服务</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500/10 flex-shrink-0">
                      <i className="ri-global-line text-accent-400 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">官方网站</div>
                      <a href="/" className="text-sm text-foreground-200 hover:text-accent-400 transition-colors cursor-pointer">
                        www.qiuxiayy.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-8 text-center">
                常见问题
              </h2>
              <div className="max-w-[800px] mx-auto space-y-3">
                {faqData.slice(0, 4).map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-100/50 rounded-xl border border-background-200/50 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <h4 className="text-sm md:text-base font-medium text-foreground-100 pr-4">
                        {faq.question}
                      </h4>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-45' : ''}`}>
                        <i className="ri-add-line text-foreground-400 text-lg"></i>
                      </div>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}